import {ChangeDueDateRequest} from 'services/types';
import {
	BnRedirect,
	CustomerUser,
	InnsalgOppslag,
	InnsalgSvar,
	OrderUpsellProductsRequest,
	PageFiltersTypes,
	ThreeSixtyStoreState,
	VismaRestkontoDownloadRequest,
} from 'common/types/store/threeSixtyTypes';
import {createState} from './createState';
import ThreeSixtyStoreModel from 'common/models/threeSixtyModel';
import {LIMIT_PAYLOAD} from 'common/constants';
import {TemplateType} from 'common/components/TrustpilotDialog';
import {DisplayPreferences, UserPreferences} from 'common/models/profileModel';

type ThreeSixtyState = ThreeSixtyStoreState & {
    [key: string]: unknown;
    resetState: () => void;
    resetAccounts: () => void;
    setBnRedirect: (value: Partial<BnRedirect>) => void;
    resetBnRedirect: () => void;
	resetInnsalg: () => void;
    resetQueryResults: () => void;
    setCurrentUser: (user: string | null) => void;
    setActiveRow: (row: CustomerUser | null) => void;
	getAccount: (params: {id: string | null; phone: string | null; onSuccess?: () => void}) => Promise<void>;
    getAccountList: (limit?: number, search?: string, sort?: string) => Promise<void>;
	sendTrustPilotReview: (params: { data: TemplateType; onSuccess?: () => void }) => Promise<void>;
    changeVismaDueDate: (requestBody: ChangeDueDateRequest, onSuccess?: () => void) => Promise<void>;
	getAccountFeed: (params: {
		id: string;
		feedType?: string | string[];
		limit?: number;
		sort?: string;
		search?: string;
		isLoading?: boolean;
	}) => Promise<void>;
	addEventComment: (params: {id: string; comment: string; onSuccess?: () => void}) => Promise<void>;
	deleteEventComment: (id: string, onSuccess?: () => void) => Promise<void>;
	getBillingGroups: (params: {id: string; limit?: number; sort?: string; search?: string}) => Promise<void>;
	getContacts: (params: {id: string; limit?: number; sort?: string; search?: string, lazy?: boolean}) => Promise<void>;
	getUsers: (params: {
		id: string;
		limit?: number;
		sort?: string;
		search?: string;
		statuses?: boolean;
		phone?: string;
		parentId?: string;
		childCards?: boolean;
	}) => Promise<void>;
	getUser: (params: {phone: string; accountId: string; onSuccess?: () => void}) => Promise<void>;
	getUserParentId: (params: {limit?: number; accountId: string; parent: string; includeChild: boolean}) => Promise<void>;
	getZendesk: (params: {limit?: number; id: string; search?: string; sort?: string}) => Promise<void>;
	majorResponsibleList: (params: {limit?: number; search?: string; sort?: string}) => Promise<void>;
	majorResponsibleSearch: (params: {entraId: string; search?: string}) => Promise<void>;
	setSatisfactionRating: (params: {accountId: string; body: string; onSuccess?: () => void; onError?: () => void}) => Promise<void>;
	sendSms: (params: {number: string; text: string; onSuccess?: () => void}) => Promise<void>;
	getConfigTemplates: (identifier?: string, invoiceNumber?: string, clientNumber?: number) => Promise<void>;
	reportIncident: (params: {data: Record<string, unknown>; onSuccess?: () => void}) => Promise<void>;
    getVismaList: (params: {
		id: string;
		page?: number;
		pageSize?: number;
		status?: string;
		billingGroups?: string[];
	}) => Promise<void>;
	getVismaInvoice: (clientNumber: string | number, invoiceNumber: string, page?: number, page_size?: number) => Promise<void>;
	downloadInvoiceLatest: (clientNumber: number, invoiceNumber: string) => Promise<void>;
	downloadInvoice: (clientNumber: number, invoiceNumber: string) => Promise<void>;
	sendPdf: (params: {
		id: string;
		clientNr: string;
		emailReceiver: string;
		invoiceNr: string;
		language: string;
		onSuccess: () => void;
	}) => Promise<void>;
	getUpsellUser: (userId: number, onSuccess?: () => void) => Promise<void>;
	getUserConfig: () => Promise<void>;
    setUserConfig: (
        key: string, 
        value: string | number | boolean | unknown[] | string[] | DisplayPreferences | UserPreferences, 
        onSuccess?: () => void
    ) => Promise<void>;
    setUserConfigBatch: (
        configs: Record<string, string | number | boolean | unknown[] | string[] | DisplayPreferences | UserPreferences>, 
        onSuccess?: () => void
    ) => Promise<void>;
	postUpsellOrder: (data: OrderUpsellProductsRequest, onSuccess?: () => void) => Promise<void>;
	postUpsellSms: (phoneNumber: string, onSuccess?: () => void) => Promise<void>;
	getInnsalg: (orgnr: string) => Promise<InnsalgOppslag | null>;
	postInnsalgSvar: (params: {visningId: string; data: InnsalgSvar; onSuccess?: () => void}) => Promise<void>;
	getVismaReskonto: (params: {data: VismaRestkontoDownloadRequest; onSuccess?: () => void}) => Promise<void>;
	updatePageFilters: (filters: Partial<PageFiltersTypes>) => void;
};
  
const useThreeSixtyNewStore = createState<ThreeSixtyState>('ThreeSixtyStore', (set) => {
	const model = new ThreeSixtyStoreModel(set);

	return {
		// Initial state
		...model.defaultState,

		// Actions
		resetState: () => model.resetState(),
		resetAccounts: () => model.resetAccounts(),
		setBnRedirect: (value: Partial<BnRedirect>) => model.setBnRedirect(value),
		resetBnRedirect: () => model.resetBnRedirect(),
		resetInnsalg: () => model.resetInnsalg(),
		resetQueryResults: () => model.resetQueryResults(),
		setCurrentUser: (user: string | null) => model.setCurrentUser(user),
		setActiveRow: (row: CustomerUser | null) => model.setActiveRow(row),
		updatePageFilters: (filters: Partial<PageFiltersTypes>) => model.updatePageFilters(filters),
        
		// Api call actions:

		// Upsell
		getUpsellUser: (userId: number, onSuccess?: () => void) => model.getUpsellUser(userId, onSuccess),
		postUpsellOrder: (data: OrderUpsellProductsRequest, onSuccess?: () => void) => model.postUpsellOrder({data, onSuccess}),
		postUpsellSms: (phoneNumber: string, onSuccess?: () => void) => model.postUpsellSms({phoneNumber, onSuccess}),

		// Innsalg
		getInnsalg: (orgnr: string) => model.getInnsalg(orgnr),
		postInnsalgSvar: (params: {visningId: string; data: InnsalgSvar; onSuccess?: () => void}) =>
			model.postInnsalgSvar(params),

		// threeSixty
		getAccountList: (limit?: number, search?: string, sort?: string) =>
			model.getAccountList(limit, search, sort),
		getAccount: ({id, phone, onSuccess}: {id: string | null; phone: string | null, onSuccess?: () => void}) =>
			model.getAccount({id, phone, onSuccess}),
		getAccountFeed: ({
			id,
			feedType = '',
			limit = LIMIT_PAYLOAD,
			sort = '',
			search = '',
			isLoading = false,
		}: {
			id: string;
			feedType?: string | string[];
			limit?: number;
			sort?: string;
			search?: string;
			isLoading?: boolean;
		}) => model.getAccountFeed({id, feedType, limit, sort, search, isLoading}),
		getBillingGroups: ({
			id,
			limit = LIMIT_PAYLOAD,
			sort = '',
			search = '',
		}: {
			id: string;
			limit?: number;
			sort?: string;
			search?: string;
		}) => model.getBillingGroups({id, limit, sort, search}),
		getContacts: ({
			id,
			limit = LIMIT_PAYLOAD,
			sort = '',
			search = '',
			lazy = false,
		}: {
			id: string;
			limit?:number;
			sort?: string;
			search?: string,
			lazy?: boolean
		}) =>
			model.getContacts({id, limit, sort, search, lazy}),
		getUsers: (
			{
				id,
				limit = LIMIT_PAYLOAD,
				sort = '',
				search = '',
				statuses = true,
				phone = '',
				parentId = '',
				childCards = false,
			}: {
				id: string;
				limit?: number;
				sort?: string;
				search?: string,
				statuses?: boolean
				phone?: string
				parentId?: string
				childCards?: boolean
			}) =>
			model.getUsers({id, limit, sort, search, statuses, phone, parentId, childCards}),
		getUser: ({phone, accountId, onSuccess}: {phone: string; accountId: string; onSuccess?: () => void}) =>
			model.getUser({phone, accountId, onSuccess}),
		getUserParentId: ({limit = LIMIT_PAYLOAD, accountId, parent, includeChild}:
			{limit?: number; accountId: string; parent: string; includeChild: boolean}) =>
			model.getUserParentId({limit, accountId, parent, includeChild}),
		getZendesk: (
			{limit = LIMIT_PAYLOAD, id, search = '', sort = ''}: 
			{limit?: number; id: string; search?: string; sort?: string}) =>
			model.getZendesk({limit, id, search, sort}),
		        // Account feed actions
		deleteEventComment: (id: string, onSuccess?: () => void) =>
			model.deleteEventComment(id, onSuccess),
		addEventComment: ({id, comment, onSuccess}: {id: string; comment: string; onSuccess?: () => void}) =>
			model.addEventComment({id, comment, onSuccess}),
		
		// Major responsible list
		majorResponsibleList: ({limit = LIMIT_PAYLOAD, search = '', sort = 'nameAsc'}:
					{limit?: number; search?: string; sort?: string}) =>
			model.majorResponsibleList({limit, search, sort}),
		majorResponsibleSearch: ({entraId, search = ''}: {entraId: string; search?: string}) =>
			model.majorResponsibleSearch({entraId, search}),
		
		// Account satisfaction
		setSatisfactionRating: (
			{accountId, body, onSuccess, onError}: {accountId: string; body: string; onSuccess?: () => void; onError?: () => void}
		) => model.setSatisfactionRating({accountId, body, onSuccess, onError}),
		
		// Send SMS
		sendSms: ({number, text, onSuccess}: {number: string; text: string; onSuccess?: () => void}) =>
			model.sendSms({number, text, onSuccess}),
		
		// Send TrustPilot Review
		sendTrustPilotReview: ({data, onSuccess}: {data: TemplateType; onSuccess?: (() => void) | undefined}) =>
			model.sendTrustPilotReview({data, onSuccess}),
		// Get Config Templates
		getConfigTemplates: (identifier = 'utland_kategorier', invoiceNumber?: string, clientNumber?: number) =>
			model.getConfigTemplates(identifier, invoiceNumber, clientNumber),
		// // Report Incident
		reportIncident: ({data, onSuccess}: {data: Record<string, unknown>; onSuccess?: () => void}) =>
			model.reportIncident({data, onSuccess}),
		// visma
		changeVismaDueDate: (requestBody: ChangeDueDateRequest, onSuccess?: () => void) =>
			model.vismaChangeDueDate(requestBody, onSuccess),
		getVismaList: (
			{
				id,
				page = 1,
				pageSize = 50,
				status,
				billingGroups,
			}: {
				id: string;
				page?: number;
				pageSize?: number;
				status?: string;
				billingGroups?: string[];
			}
		) => {
			const params = {
				id,
				page,
				pageSize,
				...(status !== undefined && {status}),
				...(billingGroups !== undefined && {billingGroups})
			};
			return model.getVismaList(params);
		},
		getVismaReskonto: (params: {data: VismaRestkontoDownloadRequest; onSuccess?: () => void}) =>
			model.getVismaReskonto(params),
		getVismaInvoice: (clientNumber: string | number, invoiceNumber: string, page?: number, pageSize?: number) =>
			model.getVismaInvoice(clientNumber, invoiceNumber, page, pageSize),
		downloadInvoiceLatest: (clientNumber: number, invoiceNumber: string) =>
			model.downloadInvoiceLatest(clientNumber, invoiceNumber),
		downloadInvoice: (clientNumber: number, invoiceNumber: string) =>
			model.downloadInvoice(clientNumber, invoiceNumber),
		sendPdf: ({id, clientNr, emailReceiver, invoiceNr, language, onSuccess}:
			{id: string; clientNr: string; emailReceiver: string; invoiceNr: string; language: string, onSuccess: () => void}) =>
			model.sendVismaPdf({id, clientNr, emailReceiver, invoiceNr, language, onSuccess}),
		getUserConfig: () => model.getUserConfig(),
		setUserConfig: (
			key: string, 
			value: string | string[] | number | boolean | unknown[] | DisplayPreferences | UserPreferences, 
			onSuccess?: () => void
		) => {
			return model.setUserConfig(key, value, onSuccess);
		},
		setUserConfigBatch: (
			configs: Record<string, string | number | boolean | unknown[] | string[] | DisplayPreferences | UserPreferences>, 
			onSuccess?: () => void
		) => {
			return model.setUserConfigBatch(configs, onSuccess);
		}
	};
});

export default useThreeSixtyNewStore;